import { CLAMP_FORECAST_PLANNED_ARRIVAL_LOCATION } from "./clamp-forecast-planned-arrivals.js";
import {
  clampForecastArrivalMonthFromIso,
  resolveClampForecastExpectedArrivalDate,
  type ClampForecastExpectedArrivalPoLink,
} from "./clamp-forecast-resolve-expected-arrival.js";

export interface ClampForecastGhostArrivalRow {
  category_code: string;
  plan_month: string;
  ghost_qty: number;
  expected_arrival_date?: string | null;
}

export interface ClampForecastGhostArrivalCell {
  id: number;
  category_code: string;
  plan_month: string;
  expected_arrival_date: string | null;
}

export interface ApplyClampForecastGhostArrivalsOptions {
  /** Legacy fallback when a stored ghost row has no expected_arrival_date. */
  cells?: ClampForecastGhostArrivalCell[];
  poLinksByForecastId?: Map<number, ClampForecastExpectedArrivalPoLink[]>;
}

export interface ClampForecastGhostArrivalDerivedRow {
  category_code: string;
  plan_month: string;
  location_code: string;
  rolled_qty_pre_order: number;
  starting_qty: number;
  arrivals: number;
  demand: number;
  pipeline_breakdown?: {
    sku: string;
    rolled_qty_pre_order: number;
    starting_qty: number;
    arrivals: number;
    demand: number;
  }[];
}

function normalizeSkuKey(sku: string): string {
  return sku.trim().toUpperCase();
}

function cellKey(categoryCode: string, planMonth: string): string {
  return `${categoryCode}::${planMonth}`;
}

function indexCellsByCategoryMonth(
  cells: ClampForecastGhostArrivalCell[],
): Map<string, ClampForecastGhostArrivalCell> {
  const map = new Map<string, ClampForecastGhostArrivalCell>();
  for (const cell of cells) {
    map.set(cellKey(cell.category_code, cell.plan_month), cell);
  }
  return map;
}

function resolveGhostArrivalDate(
  ghost: ClampForecastGhostArrivalRow,
  cellsByKey: Map<string, ClampForecastGhostArrivalCell>,
  poLinksByForecastId: Map<number, ClampForecastExpectedArrivalPoLink[]>,
): string | null {
  const explicit = ghost.expected_arrival_date?.trim() ?? "";
  if (explicit !== "") return explicit;

  const cell = cellsByKey.get(cellKey(ghost.category_code, ghost.plan_month));
  if (cell == null) return null;
  const poLinks = poLinksByForecastId.get(cell.id) ?? [];
  return resolveClampForecastExpectedArrivalDate(cell, poLinks);
}

function rechainLocationMonths(rows: ClampForecastGhostArrivalDerivedRow[]): ClampForecastGhostArrivalDerivedRow[] {
  const sorted = [...rows].sort((left, right) => left.plan_month.localeCompare(right.plan_month));
  let previousRolled: number | null = null;
  const out: ClampForecastGhostArrivalDerivedRow[] = [];
  for (const row of sorted) {
    const starting = previousRolled ?? row.starting_qty;
    const nextRow: ClampForecastGhostArrivalDerivedRow = {
      ...row,
      starting_qty: starting,
      rolled_qty_pre_order: starting + row.arrivals - row.demand,
    };
    if (nextRow.pipeline_breakdown != null) {
      nextRow.pipeline_breakdown = nextRow.pipeline_breakdown.map((part) => ({
        ...part,
        rolled_qty_pre_order: part.starting_qty + part.arrivals - part.demand,
      }));
    }
    previousRolled = nextRow.rolled_qty_pre_order;
    out.push(nextRow);
  }
  return out;
}

/**
 * Apply signed ghost shipment adjustments to derived-demand arrivals on each
 * ghost row's expected arrival month at SGS, then re-chain rolled totals
 * forward per location.
 */
export function applyClampForecastGhostArrivals<
  Row extends ClampForecastGhostArrivalDerivedRow,
>(
  ghosts: ClampForecastGhostArrivalRow[],
  derivedDemand: Row[],
  rawSkuByCategory: Map<string, string | null>,
  options: ApplyClampForecastGhostArrivalsOptions = {},
): Row[] {
  if (ghosts.length === 0 || derivedDemand.length === 0) return derivedDemand;

  const cellsByKey = indexCellsByCategoryMonth(options.cells ?? []);
  const poLinksByForecastId = options.poLinksByForecastId ?? new Map();

  const injections = new Map<string, number>();
  for (const ghost of ghosts) {
    if (ghost.ghost_qty === 0) continue;
    const arrivalDate = resolveGhostArrivalDate(ghost, cellsByKey, poLinksByForecastId);
    if (arrivalDate == null) continue;
    const arrivalMonth = clampForecastArrivalMonthFromIso(arrivalDate);
    if (arrivalMonth == null) continue;
    const key = `${ghost.category_code}::${arrivalMonth}`;
    injections.set(key, (injections.get(key) ?? 0) + ghost.ghost_qty);
  }
  if (injections.size === 0) return derivedDemand;

  const mutated = derivedDemand.map((row) => ({
    ...row,
    pipeline_breakdown: row.pipeline_breakdown?.map((part) => ({ ...part })),
  }));

  for (const row of mutated) {
    const injectionKey = `${row.category_code}::${row.plan_month}`;
    const qty = injections.get(injectionKey);
    if (qty == null || qty === 0) continue;
    if (row.location_code !== CLAMP_FORECAST_PLANNED_ARRIVAL_LOCATION) continue;

    const rawSku = rawSkuByCategory.get(row.category_code) ?? null;
    row.arrivals += qty;
    if (row.pipeline_breakdown != null && row.pipeline_breakdown.length > 0 && rawSku != null) {
      const rawKey = normalizeSkuKey(rawSku);
      const rawPart = row.pipeline_breakdown.find((part) => normalizeSkuKey(part.sku) === rawKey);
      if (rawPart != null) {
        rawPart.arrivals += qty;
      }
    }
  }

  const grouped = new Map<string, Row[]>();
  for (const row of mutated) {
    const groupKey = `${row.category_code}::${row.location_code}`;
    const list = grouped.get(groupKey) ?? [];
    list.push(row);
    grouped.set(groupKey, list);
  }

  const rechained: Row[] = [];
  for (const group of grouped.values()) {
    rechained.push(...(rechainLocationMonths(group) as Row[]));
  }
  rechained.sort((left, right) => {
    const monthDelta = left.plan_month.localeCompare(right.plan_month);
    if (monthDelta !== 0) return monthDelta;
    const categoryDelta = left.category_code.localeCompare(right.category_code);
    if (categoryDelta !== 0) return categoryDelta;
    return left.location_code.localeCompare(right.location_code);
  });
  return rechained;
}
